// Démarches configurables (H3-05, brief L1-A) : contenu complet d'une version (identité,
// fondement, éligibilité, formulaire, pièces, étapes, délais, tarif, modèle, conservation,
// identité vérifiée, canaux, textes par statut), cycle de vie et migration de version.
import { z } from 'zod';
import { champsDeCondition, schemaCondition, tousLesChamps } from './demarches-formulaire';
import { schemaFormulaireValide } from './demarches-moteur-formulaire';
import { STATUTS_USAGER, type StatutUsager } from './dossiers';

const uuid = z.string().uuid('Identifiant invalide');
const code = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés');
const texte = (max: number) => z.string().trim().max(max);

/** Code de démarche : 40 caractères au plus (le code de la définition de workflow en dérive). */
export const schemaCodeDemarche = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Code en minuscules, chiffres et soulignés (40 caractères au plus)');

export const ETATS_DEMARCHE = ['brouillon', 'en_verification', 'publiee', 'suspendue', 'retiree'] as const;
export const ETATS_VERSION_DEMARCHE = ['brouillon', 'en_verification', 'publiee', 'remplacee'] as const;
export type EtatVersionDemarche = (typeof ETATS_VERSION_DEMARCHE)[number];

/** Nature d'une étape : chaque étape devient un état en_instruction, en_validation ou en_attente_decision. */
export const NATURES_ETAPE = ['instruction', 'validation', 'decision'] as const;
export type NatureEtape = (typeof NATURES_ETAPE)[number];
export const PREFIXES_ETAT_ETAPE: Record<NatureEtape, string> = {
  instruction: 'en_instruction',
  validation: 'en_validation',
  decision: 'en_attente_decision',
};

export const CANAUX_NOTIFICATION_DEMARCHE = ['application', 'portail', 'courriel', 'sms'] as const;
export const MOMENTS_PAIEMENT = ['aucun', 'avant_instruction', 'avant_delivrance'] as const;
export const UNITES_DELAI = ['calendaires', 'ouvres'] as const;

export const schemaEtapeDemarche = z.strictObject({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'Code d\'étape en minuscules (31 caractères au plus)'),
  libelle: z.string().trim().min(2).max(120),
  nature: z.enum(NATURES_ETAPE),
  /** Service chargé de l'étape : le dossier lui est affecté à l'entrée dans l'étape. */
  service_id: uuid.nullable().default(null),
  /** Rôle attendu pour agir (indicatif : la permission découle de la nature de l'étape). */
  role_code: code.nullable().default(null),
  delai_interne_jours: z.number().int().min(1).max(365).nullable().default(null),
  /** Actions exécutées à l'entrée dans l'étape. */
  actions: z.array(z.enum(['notifier'])).max(1).default([]),
});
export type EtapeDemarche = z.infer<typeof schemaEtapeDemarche>;

export const schemaPieceDemarche = z.strictObject({
  type: code,
  libelle: z.string().trim().min(2).max(200),
  obligatoire: z.boolean().default(true),
  /** La pièce n'est demandée que si la condition sur les données du formulaire est remplie. */
  condition: schemaCondition.optional(),
  aide: texte(1000).optional(),
});
export type PieceDemarche = z.infer<typeof schemaPieceDemarche>;

export const schemaRegleEligibilite = z.strictObject({
  condition: schemaCondition,
  message: z.string().trim().min(3).max(300),
});

export const schemaDelaiDemarche = z.strictObject({
  jours: z.number().int().min(1).max(3650),
  unite: z.enum(UNITES_DELAI),
});

export const schemaContenuVersion = z
  .strictObject({
    libelle: z.string().trim().min(3).max(200),
    description: texte(5000).default(''),
    domaine: code.default('dossier'),
    autorite_responsable: z.string().trim().min(2).max(200),
    /** Obligatoire ; « à confirmer » est admis (H3-05). */
    fondement: z.string().trim().min(3, 'Le fondement est obligatoire (« à confirmer » est admis)').max(2000),
    conditions_eligibilite: z
      .strictObject({ texte: texte(5000).default(''), regles: z.array(schemaRegleEligibilite).max(20).default([]) })
      .default({ texte: '', regles: [] }),
    formulaire: schemaFormulaireValide,
    pieces: z.array(schemaPieceDemarche).max(30).default([]),
    etapes: z.array(schemaEtapeDemarche).min(1, 'Au moins une étape est requise').max(10),
    delais: z
      .strictObject({
        reglementaire: schemaDelaiDemarche.nullable(),
        interne: schemaDelaiDemarche.nullable(),
        relance_complement_jours: z.number().int().min(1).max(365).default(15),
        classement_sans_suite_jours: z.number().int().min(2).max(730).default(45),
      })
      .refine((d) => d.classement_sans_suite_jours > d.relance_complement_jours, {
        message: 'Le classement sans suite suit la relance',
        path: ['classement_sans_suite_jours'],
      }),
    /** Tarif (lot L2) : champ enregistré, sans effet sur le dossier tant que les recettes ne sont pas livrées. */
    tarif: z
      .strictObject({
        rubrique: code.nullable().default(null),
        montant_indicatif: z.number().int().min(0).nullable().default(null),
        moment_paiement: z.enum(MOMENTS_PAIEMENT).default('aucun'),
      })
      .default({ rubrique: null, montant_indicatif: null, moment_paiement: 'aucun' }),
    modele_document_code: code.nullable().default(null),
    regle_conservation_id: uuid.nullable().default(null),
    identite_verifiee_requise: z.boolean().default(false),
    canaux_notification: z.array(z.enum(CANAUX_NOTIFICATION_DEMARCHE)).min(1).max(4).default(['portail']),
    textes_statuts: z.partialRecord(z.enum(Object.keys(STATUTS_USAGER) as [StatutUsager, ...StatutUsager[]]), texte(1000)).default({}),
  })
  .superRefine((v, ctx) => {
    const codesEtapes = new Set<string>();
    v.etapes.forEach((e, i) => {
      if (codesEtapes.has(e.code)) ctx.addIssue({ code: 'custom', message: `Étape en double : ${e.code}`, path: ['etapes', i, 'code'] });
      codesEtapes.add(e.code);
      if (e.nature === 'decision' && i !== v.etapes.length - 1) {
        ctx.addIssue({ code: 'custom', message: 'L\'étape de décision est la dernière', path: ['etapes', i, 'nature'] });
      }
    });
    if (v.etapes.at(-1)?.nature !== 'decision') ctx.addIssue({ code: 'custom', message: 'La dernière étape est une décision', path: ['etapes'] });
    const champs = new Set(tousLesChamps(v.formulaire).map((x) => x.champ.code));
    const types = new Set<string>();
    v.pieces.forEach((p, i) => {
      if (types.has(p.type)) ctx.addIssue({ code: 'custom', message: `Pièce en double : ${p.type}`, path: ['pieces', i, 'type'] });
      types.add(p.type);
      for (const ref of champsDeCondition(p.condition)) {
        if (!champs.has(ref)) ctx.addIssue({ code: 'custom', message: `Condition sur un champ inconnu : ${ref}`, path: ['pieces', i, 'condition'] });
      }
    });
    v.conditions_eligibilite.regles.forEach((r, i) => {
      for (const ref of champsDeCondition(r.condition)) {
        if (!champs.has(ref)) {
          ctx.addIssue({ code: 'custom', message: `Condition sur un champ inconnu : ${ref}`, path: ['conditions_eligibilite', 'regles', i] });
        }
      }
    });
  });
export type ContenuVersion = z.infer<typeof schemaContenuVersion>;

// Entrées ------------------------------------------------------------------------------------

export const schemaCreationDemarche = z.strictObject({
  code: schemaCodeDemarche,
  libelle: z.string().trim().min(3).max(200),
  description: texte(5000).optional(),
  domaine: code.optional(),
  autorite_responsable: z.string().trim().min(2).max(200).optional(),
});

export const schemaModificationVersion = z.strictObject({
  contenu: schemaContenuVersion,
  motif: texte(500).optional(),
});

export const schemaMotifDemarche = z.strictObject({ motif: z.string().trim().min(3, 'Motif obligatoire').max(1000) });
export const schemaMotifFacultatif = z.strictObject({ motif: texte(1000).optional() });

export const schemaMigrationDossier = z.strictObject({
  /** Version cible ; par défaut la version publiée courante de la démarche. */
  version_id: uuid.optional(),
  motif: z.string().trim().min(3, 'Motif obligatoire').max(1000),
});

export const schemaMigrationLot = z.strictObject({
  motif: z.string().trim().min(3, 'Motif obligatoire').max(1000),
  /** Versions sources ; par défaut toutes les versions antérieures. */
  versions_source: z.array(uuid).max(50).optional(),
});

export const schemaParametresVersion = z.object({ id: uuid, v: z.coerce.number().int().min(1).max(100000) });

// Réponses -----------------------------------------------------------------------------------

export const schemaResumeVersion = z.object({
  id: uuid,
  numero: z.number(),
  etat: z.enum(ETATS_VERSION_DEMARCHE),
  publiee_le: z.string().nullable(),
});

export const schemaDemarche = z.object({
  id: uuid,
  code: z.string(),
  libelle: z.string(),
  description: z.string().nullable(),
  domaine: z.string(),
  etat: z.enum(ETATS_DEMARCHE),
  interne: z.boolean(),
  motif_etat: z.string().nullable(),
  version_courante: schemaResumeVersion.nullable(),
  version_en_cours: schemaResumeVersion.nullable(),
  versions: z.array(schemaResumeVersion),
});
export type Demarche = z.infer<typeof schemaDemarche>;

export const schemaVersionDemarche = schemaResumeVersion.extend({
  demarche_id: uuid,
  contenu: z.record(z.string(), z.unknown()),
  soumise_le: z.string().nullable(),
  publiee_par: z.string().nullable(),
  workflow_definition_id: z.string().nullable(),
});

export const schemaRapportMigration = z.object({
  version_cible: z.object({ id: uuid, numero: z.number() }),
  migres: z.array(z.object({ dossier_id: uuid, numero: z.string().nullable(), version_source: z.number(), avertissements: z.array(z.string()) })),
  refuses: z.array(z.object({ dossier_id: uuid, numero: z.string().nullable(), version_source: z.number(), motif: z.string(), etats_absents: z.array(z.string()) })),
});
export type RapportMigration = z.infer<typeof schemaRapportMigration>;
